import { inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { extractGroupedPermissionsPayload } from '../utils/permission-response.util';
import { PermissionConfigService } from './permission-config.service';
import { UserPermissionApiService } from './user-permission-api.service';

export interface UserPermissionsSnapshot {
  roles: string[];
  permissions: string[];
}

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly userPermissionApi = inject(UserPermissionApiService);
  private readonly permissionConfig = inject(PermissionConfigService);

  private readonly subject = new BehaviorSubject<UserPermissionsSnapshot>({
    roles: [],
    permissions: [],
  });

  readonly userPermissions$ = this.subject.asObservable();

  /** Emits whenever bootstrap completes or permission slugs change (for impure pipes). */
  private readonly revision$ = new BehaviorSubject(0);
  readonly permissionsRevision$ = this.revision$.asObservable();

  private readonly permissionCache = new Map<string, boolean>();
  private readonly roleCache = new Map<string, boolean>();

  private readonly _permissionRevision = signal(0);
  /** Read in templates/pipes to establish reactive dependency on permission changes. */
  readonly permissionRevision = this._permissionRevision.asReadonly();

  /** First `/permission/user` attempt finished (success or failure). */
  private readonly _bootstrapDone = signal(false);
  /** Reactive signal — templates reading this are automatically updated when bootstrap completes. */
  readonly bootstrapDone = this._bootstrapDone.asReadonly();

  private inflight: Observable<void> | null = null;

  /**
   * Loads permission slugs from the grouped API payload (same shape as manage-permissions screen).
   */
  applyGroupedPermissions(data: Record<string, unknown> | null | undefined): void {
    const slugs = collectPermissionSlugsFromGrouped(data);
    const unique = [...new Set(slugs)];
    this.subject.next({ roles: this.subject.value.roles, permissions: unique });
    this.clearCache();
    this.revision$.next(this.revision$.value + 1);
    this._permissionRevision.update((n) => n + 1);
    if (!environment.production) {
      console.debug('[PermissionService] loaded slugs:', unique);
    }
  }

  applyRolesFromUserProfile(userData: unknown): void {
    const names: string[] = [];
    const u = userData as { user_roles?: unknown[] } | null | undefined;
    if (u?.user_roles && Array.isArray(u.user_roles)) {
      for (const ur of u.user_roles) {
        const role = (ur as { role?: { role_name?: string } })?.role;
        if (role?.role_name) names.push(role.role_name);
      }
    }
    this.subject.next({ roles: [...new Set(names)], permissions: this.subject.value.permissions });
    this.clearCache();
  }

  get initialPermissionFetchComplete(): boolean {
    return this._bootstrapDone();
  }

  /** Same rules as the `hasPermission` pipe (includes bypass flags). Signal-backed for templates. */
  can(slug: string): boolean {
    this._permissionRevision();
    const c = this.permissionConfig.getConfig();
    if (!c.enablePermissionChecks || c.bypassAllPermissions) {
      return true;
    }
    if (!slug) {
      return true;
    }
    return this.hasPermission(slug);
  }

  canAny(slugs: string[]): boolean {
    this._permissionRevision();
    const c = this.permissionConfig.getConfig();
    if (!c.enablePermissionChecks || c.bypassAllPermissions) {
      return true;
    }
    return slugs.some((s) => this.hasPermission(s));
  }

  /** Same rules as the `hasPermission` pipe (includes bypass flags). */
  isAccessAllowed(permission: string | string[], requireAll = false): boolean {
    const c = this.permissionConfig.getConfig();
    if (!c.enablePermissionChecks || c.bypassAllPermissions) {
      return true;
    }
    if (!permission) {
      return true;
    }
    const list = Array.isArray(permission) ? permission : [permission];
    return requireAll ? this.hasAllPermissions(list) : this.hasAnyPermission(list);
  }

  hasPermission(slug: string): boolean {
    const key = `p:${slug}`;
    if (this.permissionCache.has(key)) return this.permissionCache.get(key)!;
    const ok = this.subject.value.permissions.includes(slug);
    this.permissionCache.set(key, ok);
    return ok;
  }

  hasAnyPermission(slugs: string[]): boolean {
    return slugs.some((s) => this.subject.value.permissions.includes(s));
  }

  hasAllPermissions(slugs: string[]): boolean {
    return slugs.every((s) => this.subject.value.permissions.includes(s));
  }

  hasRole(name: string): boolean {
    const key = `r:${name}`;
    if (this.roleCache.has(key)) return this.roleCache.get(key)!;
    const ok = this.subject.value.roles.includes(name);
    this.roleCache.set(key, ok);
    return ok;
  }

  hasAnyRole(names: string[]): boolean {
    return names.some((n) => this.subject.value.roles.includes(n));
  }

  hasAllRoles(names: string[]): boolean {
    return names.every((n) => this.subject.value.roles.includes(n));
  }

  getCurrentPermissions(): UserPermissionsSnapshot {
    return this.subject.value;
  }

  clearPermissions(): void {
    this.subject.next({ roles: [], permissions: [] });
    this._bootstrapDone.set(false);
    this.inflight = null;
    this.clearCache();
  }

  /**
   * Re-fetches permission slugs from the server (e.g. after role permissions change via WebSocket).
   */
  refreshPermissions(): Observable<void> {
    return this.userPermissionApi.fetchUserPermissionsGrouped().pipe(
      tap((res) => {
        this.applyGroupedPermissions(extractGroupedPermissionsPayload(res) ?? {});
      }),
      // Must not rethrow. This runs from the 15s RbacLiveSync poll and from the
      // `permissions:updated` socket event: an error here would terminate the polling
      // subscription for good, and would surface a background 401 to the session
      // interceptor — bouncing the user to /auth/signin mid-session. A failed refresh
      // should simply leave the previously loaded permissions in place.
      catchError(() => of(undefined)),
      map(() => undefined),
    );
  }

  private finishBootstrap(res: unknown): void {
    const grouped = extractGroupedPermissionsPayload(res);
    this.applyGroupedPermissions(grouped ?? {});
    this._bootstrapDone.set(true);
    this.revision$.next(this.revision$.value + 1);
  }

  /**
   * Ensures `/permission/user` ran at least once for this session (cookie auth).
   */
  ensureLoaded(): Observable<void> {
    if (this._bootstrapDone()) {
      return of(undefined);
    }
    this.inflight ??= this.userPermissionApi.fetchUserPermissionsGrouped().pipe(
        tap((res) => this.finishBootstrap(res)),
        catchError(() => {
          this.applyGroupedPermissions({});
          this._bootstrapDone.set(true);
          this.revision$.next(this.revision$.value + 1);
          return of(undefined);
        }),
        map(() => undefined),
        finalize(() => {
          this.inflight = null;
        }),
        shareReplay(1),
      );
    return this.inflight;
  }

  private clearCache(): void {
    this.permissionCache.clear();
    this.roleCache.clear();
  }
}

function collectPermissionSlugsFromGrouped(data: Record<string, unknown> | null | undefined): string[] {
  const slugs: string[] = [];
  if (!data || typeof data !== 'object') {
    return slugs;
  }
  for (const rows of Object.values(data)) {
    if (!Array.isArray(rows)) continue;
    for (const row of rows) {
      if (row && typeof row === 'object' && typeof (row as { slug?: string }).slug === 'string') {
        slugs.push((row as { slug: string }).slug);
      }
    }
  }
  return slugs;
}
